import React, { useCallback, useRef, useState } from "react";
import SectionHeading from "../SectionHeading";
import FilterTabs from "./FilterTabs";
import GalleryGrid from "./GalleryGrid";
import GalleryLightbox from "./GalleryLightbox";
import useGalleryFilter from "./useGalleryFilter";
import useLightboxKeyboard from "./useLightboxKeyboard";
import useVideoThumbnails from "./useVideoThumbnails";
import { FILTERS } from "./galleryData";

const GallerySection = ({
  title = "Gallery",
  eyebrow = "Moments From The Pitch",
  intro = "Training intensity, academy culture, and matchday energy all in one place.",
  className = "",
}) => {
  const { activeFilter, setActiveFilter, filteredItems } = useGalleryFilter();
  const [activeIndex, setActiveIndex] = useState(null);
  const touchStartXRef = useRef(0);
  const videoThumbnails = useVideoThumbnails();

  const activeItem = activeIndex === null ? null : filteredItems[activeIndex];

  const openLightbox = useCallback((index) => {
    setActiveIndex(index);
  }, []);

  const closeLightbox = useCallback(() => {
    setActiveIndex(null);
  }, []);

  const handleFilterChange = useCallback(
    (filterId) => {
      setActiveFilter(filterId);
      setActiveIndex(null);
    },
    [setActiveFilter],
  );

  const showPrevious = useCallback(() => {
    setActiveIndex((current) => {
      if (current === null) return current;
      return current === 0 ? filteredItems.length - 1 : current - 1;
    });
  }, [filteredItems.length]);

  const showNext = useCallback(() => {
    setActiveIndex((current) => {
      if (current === null) return current;
      return current === filteredItems.length - 1 ? 0 : current + 1;
    });
  }, [filteredItems.length]);

  const handleTouchStart = useCallback((event) => {
    touchStartXRef.current = event.changedTouches[0]?.clientX ?? 0;
  }, []);

  const handleTouchEnd = useCallback(
    (event) => {
      const touchEndX = event.changedTouches[0]?.clientX ?? 0;
      const deltaX = touchEndX - touchStartXRef.current;

      if (Math.abs(deltaX) < 50) return;
      if (deltaX > 0) showPrevious();
      if (deltaX < 0) showNext();
    },
    [showNext, showPrevious],
  );

  useLightboxKeyboard(activeIndex, {
    onClose: closeLightbox,
    onPrev: showPrevious,
    onNext: showNext,
  });

  return (
    <section className={`py-16 md:py-20 lg:py-24 bg-[#f5f5f5] ${className}`}>
      <div className="max-w-7xl mx-auto site-gutter">
        <div className="mb-10 flex flex-col gap-6 xl:mb-12 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-2xl lg:max-w-3xl">
            <p className="eyebrow-badge">{eyebrow}</p>
            <SectionHeading centered={false} className="mb-0 mt-3">
              {title}
            </SectionHeading>
            <p className="body-text text-gray-600 mt-5">{intro}</p>
          </div>

          <FilterTabs
            filters={FILTERS}
            activeFilter={activeFilter}
            onChange={handleFilterChange}
          />
        </div>

        {filteredItems.length ? (
          <GalleryGrid
            key={activeFilter}
            items={filteredItems}
            videoThumbnails={videoThumbnails}
            onOpen={openLightbox}
          />
        ) : (
          <div className="surface-card rounded-3xl p-10 text-center shadow-soft">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#7D192A]/10 text-[#7D192A]">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className="h-6 w-6"
                aria-hidden="true"
              >
                <path d="M4 5h16v14H4z" fill="none" />
                <path d="M6 7h12v10H6z" fill="currentColor" />
              </svg>
            </div>
            <p className="font-inter font-black uppercase tracking-[0.2em] text-sm md:text-base text-gray-600">
              No media in this filter yet
            </p>
          </div>
        )}
      </div>

      <GalleryLightbox
        activeItem={activeItem}
        filteredItems={filteredItems}
        onClose={closeLightbox}
        onPrev={showPrevious}
        onNext={showNext}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      />
    </section>
  );
};

export default GallerySection;
